import {
  extractImageGeneratorPalette,
  getImageGeneratorAssetPreview,
  getImageGeneratorCatalog,
  getImageGeneratorDenimCatalog,
  getImageGeneratorErrorMessage,
  getImageGeneratorSeasons,
} from "../../../app/api/imageGenerator";
import type {
  ImageGeneratorCatalog,
  ImageGeneratorCatalogAsset,
  ImageGeneratorCatalogColourStory,
  ImageGeneratorCatalogPatternTrend,
  ImageGeneratorDenimCatalog,
  ImageGeneratorDenimTile,
  ImageGeneratorExtractedSwatch,
  ImageGeneratorExtractPaletteResult,
  ImageGeneratorSeason,
} from "../../../app/api/imageGenerator";
import {
  DENIM_MATERIALS,
  DENIM_PALETTES,
  DENIM_WASHES,
  denimAssetApiPath,
} from "../../../constants/imageGeneratorDictionaries";

export type GenerativeCatalog = ImageGeneratorCatalog;
export type GenerativeCatalogAsset = ImageGeneratorCatalogAsset;
export type GenerativeCatalogColourStory = ImageGeneratorCatalogColourStory;
export type GenerativeCatalogPatternTrend = ImageGeneratorCatalogPatternTrend;
export type GenerativeDenimCatalog = ImageGeneratorDenimCatalog;
export type GenerativeDenimTile = ImageGeneratorDenimTile;
export type GenerativeSeason = ImageGeneratorSeason;
export type GenerativeExtractedSwatch = ImageGeneratorExtractedSwatch;
export type GenerativeExtractPaletteResult = ImageGeneratorExtractPaletteResult;

export type GenerativeMappedPaletteSwatch = {
  hex: string;
  name: string;
  tier: "major" | "minor";
  pantone: string | null;
};

export type GenerativeMappedPalette = {
  hexes: string[];
  title: string;
  swatches: GenerativeMappedPaletteSwatch[];
};

export const PALETTE_EXTRACT_ACCEPT = "image/jpeg,image/png,image/webp";
export const PALETTE_EXTRACT_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];
export const PALETTE_EXTRACT_MAX_BYTES = 8 * 1024 * 1024;

export const paletteExtractFileError = (file: File) => {
  if (!PALETTE_EXTRACT_MIME_TYPES.includes(file.type)) {
    return "Upload a JPEG, PNG, or WebP image.";
  }
  if (file.size > PALETTE_EXTRACT_MAX_BYTES) {
    return "Image must be 8MB or smaller.";
  }
  return null;
};

export const mapExtractedPalette = (
  extracted: ImageGeneratorExtractPaletteResult | null | undefined
): GenerativeMappedPalette => {
  const sourceSwatches = extracted?.swatches?.length
    ? extracted.swatches
    : (extracted?.hexes || []).map(hex => ({
        hex,
        name: "",
        accuracy: 0,
        tier: "major" as const,
        pantone: null,
      }));
  const swatches: GenerativeMappedPaletteSwatch[] = sourceSwatches
    .filter(item => Boolean(item?.hex))
    .map(item => ({
      hex: item.hex,
      name: (item.name || "").trim(),
      tier: item.tier === "minor" ? "minor" : "major",
      pantone: item.pantone || null,
    }));
  const hexes = (
    extracted?.hexes?.length ? extracted.hexes : swatches.map(item => item.hex)
  ).filter(Boolean);
  const names = swatches.map(item => item.name).filter(Boolean);
  const pantones = swatches
    .map(item => (item.pantone || "").trim())
    .filter(Boolean);
  const title =
    names.slice(0, 5).join(" · ") ||
    pantones.slice(0, 5).join(" · ") ||
    "Uploaded palette";
  return { hexes, title, swatches };
};

export const getGenerativeCatalog = getImageGeneratorCatalog;
export const getGenerativeSeasons = getImageGeneratorSeasons;
export const getGenerativeAssetPreview = getImageGeneratorAssetPreview;
export const extractGenerativePalette = extractImageGeneratorPalette;
export const getGenerativeApiErrorMessage = getImageGeneratorErrorMessage;

export const EMPTY_DENIM_CATALOG: GenerativeDenimCatalog = {
  palettes: [],
  materials: [],
  washes: [],
};

export const FALLBACK_DENIM_CATALOG: GenerativeDenimCatalog = {
  palettes: DENIM_PALETTES.map(item => ({
    id: item.id,
    label: item.label,
    hexes: [...item.hexes],
    colourStoryId: item.id,
  })),
  materials: DENIM_MATERIALS.map(item => ({
    id: item.id,
    label: item.label,
    kind: "materials",
    url: denimAssetApiPath("materials", item.id),
  })),
  washes: DENIM_WASHES.map(item => ({
    id: item.id,
    label: item.label,
    kind: "washes",
    url: denimAssetApiPath("washes", item.id),
  })),
};

/** Authenticated denim proxy path — never use a private S3 URL as img src. */
export const denimTilePreviewPath = (item: {
  id: string;
  kind: "materials" | "washes";
  url?: string;
}) => {
  const url = item.url?.trim() || "";
  if (
    url &&
    !/^https?:\/\//i.test(url) &&
    !/^s3:\/\//i.test(url) &&
    !url.startsWith("data:") &&
    !url.startsWith("blob:")
  ) {
    return url.replace(/^\//, "");
  }
  return denimAssetApiPath(item.kind, item.id);
};

const normalizeDenimTiles = (
  items: ImageGeneratorDenimTile[] | undefined,
  kind: "materials" | "washes"
): ImageGeneratorDenimTile[] =>
  (items || []).map(item => {
    const tileKind = item.kind || kind;
    return {
      ...item,
      kind: tileKind,
      url: denimTilePreviewPath({
        id: item.id,
        kind: tileKind,
        url: item.url,
      }),
    };
  });

export const normalizeDenimCatalog = (
  catalog: GenerativeDenimCatalog | null | undefined
): GenerativeDenimCatalog => ({
  palettes: catalog?.palettes || [],
  materials: normalizeDenimTiles(catalog?.materials, "materials"),
  washes: normalizeDenimTiles(catalog?.washes, "washes"),
});

export const getGenerativeDenimCatalog = async () => {
  const next = await getImageGeneratorDenimCatalog();
  if (!next) {
    return null;
  }
  const normalized = normalizeDenimCatalog(next);
  if (
    !normalized.palettes.length &&
    !normalized.materials.length &&
    !normalized.washes.length
  ) {
    return null;
  }
  return normalized;
};
